import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { HealthJourney } from '../model/HealthJourney';
import {
  checkIsabelDiagnoses,
  fetchIsabelCountries,
  getKnowledgeUrls,
  getTriageScore,
  saveToMyJourney,
} from './actions';
import {
  IsabelCheckSymptomsResponse,
  IsabelCountries,
  IsabelSimpleSymptom,
  IsabelTriageScoreResponse,
} from './models';
import { IsabelKnowledgeUrlsResponse } from './models/IsabelKnowledgeUrlsResponse';

export interface IsabelState {
  diagnoses: IsabelCheckSymptomsResponse;
  loadingDiagnoses: boolean;
  countries: IsabelCountries;
  triageScore: IsabelTriageScoreResponse;
  loadingTriageScore: boolean;
  isabelCurrentJourney?: HealthJourney;
  knowledgeUrls: IsabelKnowledgeUrlsResponse;
  loadingKnowledgeUrls: boolean;
  saveToMyJourneyLoading: boolean;
  isabelSymptoms: IsabelSimpleSymptom[];
  isConditionModalOpen: boolean;
}

const initialState: IsabelState = {
  diagnoses: {
    diagnoses_checklist: {
      triage_api_url: '',
      query_result_details: {
        age_group: '',
        gender: '',
        pregnancy: '',
        region: '',
        specialty_type: '',
        query_entered: '',
        sorting: '',
        total_results_returned: '',
        unrecognised_symptoms: [],
      },
      diagnoses: [],
    },
  },
  loadingDiagnoses: false,
  countries: {
    countries: {
      country: [],
    },
  },
  triageScore: {
    where_to_now: {
      triage_score: '0',
    },
  },
  loadingTriageScore: false,
  knowledgeUrls: {
    knowledge_urls: [],
  },
  loadingKnowledgeUrls: false,
  saveToMyJourneyLoading: false,
  isabelSymptoms: [],
  isabelCurrentJourney: undefined,
  isConditionModalOpen: false,
};

const isabelSlice = createSlice({
  name: 'isabel',
  initialState,
  reducers: {
    setConditionModalOpen: (state, action: PayloadAction<boolean>) => {
      state.isConditionModalOpen = action.payload;
    },
    clearIsabelDiagnoses: (state) => {
      state.diagnoses = initialState.diagnoses;
    },
    clearIsabelTriageScore: (state) => {
      state.triageScore = initialState.triageScore;
    },
    clearIsabelKnowledgeUrls: (state) => {
      state.knowledgeUrls = initialState.knowledgeUrls;
    },
    clearIsabelSymptoms: (state) => {
      state.isabelSymptoms = initialState.isabelSymptoms;
    },
    clearJourneyProgress: (state) => {
      state.diagnoses = initialState.diagnoses;
      state.triageScore = initialState.triageScore;
      state.knowledgeUrls = initialState.knowledgeUrls;
      state.isabelSymptoms = initialState.isabelSymptoms;
    },
    setIsabelCurrentJourney: (
      state,
      action: PayloadAction<HealthJourney | undefined>
    ) => {
      state.isabelCurrentJourney = action.payload;
    },
    setIsabelSymptoms: (
      state,
      action: PayloadAction<IsabelSimpleSymptom[]>
    ) => {
      state.isabelSymptoms = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(checkIsabelDiagnoses.pending, (state) => {
        state.loadingDiagnoses = true;
      })
      .addCase(checkIsabelDiagnoses.fulfilled, (state, action) => {
        state.diagnoses = action.payload;
        state.loadingDiagnoses = false;
      })
      .addCase(checkIsabelDiagnoses.rejected, (state) => {
        state.loadingDiagnoses = false;
      })
      .addCase(fetchIsabelCountries.fulfilled, (state, action) => {
        state.countries = action.payload;
      })
      .addCase(getTriageScore.pending, (state) => {
        state.loadingTriageScore = true;
      })
      .addCase(getTriageScore.fulfilled, (state, action) => {
        state.triageScore = action.payload;
        state.loadingTriageScore = false;
      })
      .addCase(getKnowledgeUrls.pending, (state) => {
        state.loadingKnowledgeUrls = true;
      })
      .addCase(getKnowledgeUrls.fulfilled, (state, action) => {
        state.knowledgeUrls = action.payload;
        state.loadingKnowledgeUrls = false;
      })
      .addCase(saveToMyJourney.pending, (state) => {
        state.saveToMyJourneyLoading = true;
      })
      .addCase(saveToMyJourney.fulfilled, (state) => {
        state.saveToMyJourneyLoading = false;
      })
      .addCase(saveToMyJourney.rejected, (state) => {
        state.saveToMyJourneyLoading = false;
      });
  },
});

export default isabelSlice.reducer;

export const {
  clearIsabelDiagnoses,
  clearIsabelTriageScore,
  clearIsabelKnowledgeUrls,
  clearIsabelSymptoms,
  clearJourneyProgress,
  setIsabelCurrentJourney,
  setIsabelSymptoms,
  setConditionModalOpen,
} = isabelSlice.actions;
